import { useState, useEffect } from "react";
import {
  Link,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { api, formatINR, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import {
  Package,
  ShoppingBag,
  MessageSquare,
  Settings,
  Bell,
  Mail,
  LogOut,
  Plus,
  Trash2,
  Edit,
  X,
  Download,
  Car,
  ClipboardList,
  GripVertical,
  Upload,
  Star,
  Save,
  Sparkles,
  Image as ImageIcon,
  Power,
} from "lucide-react";

function ImagePreview({ image, alt, onClose }) {
  if (!image) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 p-2 text-white"
        aria-label="Close image preview"
      >
        <X size={24} />
      </button>
      <img
        src={image}
        alt={alt}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[90vh] max-w-full object-contain"
      />
    </div>
  );
}

function AdminLogin({ onAuthed }) {
  const [email, setEmail] = useState("admin@streetauto.in");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { email, password });
      // httpOnly cookie is now set by the server; nothing to persist client-side.
      toast.success("Welcome, admin");
      onAuthed();
    } catch {
      toast.error("Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#050505]">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#111] border border-white/10 p-8"
        data-testid="admin-login-form"
      >
        <div className="overline text-[#E63946] mb-3">Admin</div>
        <h1 className="font-display text-3xl mb-6">STREET AUTO</h1>
        <input
          required
          type="email"
          placeholder="Email"
          className="sa-input mb-3"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email"
        />
        <input
          required
          type="password"
          placeholder="Password"
          className="sa-input mb-6"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password"
        />
        <button
          disabled={loading}
          className="sa-btn sa-btn-primary w-full"
          data-testid="admin-login-submit"
        >
          {loading ? "SIGNING IN..." : "SIGN IN"}
        </button>
      </form>
    </div>
  );
}

function ProductForm({ onSave, initial, onCancel }) {
  const { categories } = useApp();
  const [f, setF] = useState(() => ({
    name: "",
    price: "",
    mrp: "",
    category: categories[0]?.name || "Interior Accessories",
    subcategory: "",
    brand: "Street Auto",
    stock: 10,
    description: "",
    images: [""],
    features_text: "",
    specs_text: "",
    included_text: "",
    important_info: "",
    tags: [],
    is_active: true,
    ...(initial
      ? {
          ...initial,
          images: initial.images?.length ? initial.images : [""],
          features_text: (initial.features || []).join("\n"),
          specs_text: Object.entries(initial.specs || {})
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n"),
          included_text: (initial.whats_included || []).join("\n"),
          tags: initial.tags || [],
        }
      : {}),
  }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: f.name,
      price: Number(f.price),
      mrp: Number(f.mrp),
      category: f.category,
      subcategory: f.subcategory,
      brand: f.brand,
      stock: Number(f.stock),
      description: f.description,
      images: f.images.filter(Boolean),
      features: f.features_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      specs: Object.fromEntries(
        f.specs_text
          .split("\n")
          .map((l) => {
            const [k, ...rest] = l.split(":");
            return [k?.trim(), rest.join(":").trim()];
          })
          .filter(([k]) => k),
      ),
      whats_included: f.included_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      important_info: f.important_info,
      tags: f.tags,
      is_active: !!f.is_active,
    };
    await onSave(payload);
  };

  const activeCat = categories.find((c) => c.name === f.category);

  return (
    <form
      onSubmit={submit}
      className="space-y-4 bg-[#111] border border-white/10 p-6"
      data-testid="product-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl">
          {initial ? "Edit Product" : "New Product"}
        </h3>
        <button type="button" onClick={onCancel} className="p-2">
          <X size={18} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          required
          placeholder="Name"
          className="sa-input sm:col-span-2"
          value={f.name}
          onChange={(e) => set("name", e.target.value)}
        />
        <input
          required
          type="number"
          placeholder="Price (₹)"
          className="sa-input"
          value={f.price}
          onChange={(e) => set("price", e.target.value)}
        />
        <input
          required
          type="number"
          placeholder="MRP (₹)"
          className="sa-input"
          value={f.mrp}
          onChange={(e) => set("mrp", e.target.value)}
        />
        <select
          className="sa-input"
          value={f.category}
          onChange={(e) => {
            set("category", e.target.value);
            set("subcategory", "");
          }}
        >
          {categories.map((c) => (
            <option key={c.slug} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="sa-input"
          value={f.subcategory}
          onChange={(e) => set("subcategory", e.target.value)}
        >
          <option value="">-- Subcategory --</option>
          {(activeCat?.subcategories || []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          placeholder="Brand"
          className="sa-input"
          value={f.brand}
          onChange={(e) => set("brand", e.target.value)}
        />
        <input
          type="number"
          placeholder="Stock"
          className="sa-input"
          value={f.stock}
          onChange={(e) => set("stock", e.target.value)}
        />
      </div>
      <textarea
        rows={3}
        placeholder="Description"
        className="sa-input"
        value={f.description}
        onChange={(e) => set("description", e.target.value)}
      ></textarea>

      <div>
        <div className="overline text-white/50 mb-2">Images (URLs)</div>
        {f.images.map((u, i) => (
          <div key={`img-${i}`} className="flex gap-2 mb-2">
            <input
              value={u}
              onChange={(e) => {
                const n = [...f.images];
                n[i] = e.target.value;
                set("images", n);
              }}
              placeholder="https://..."
              className="sa-input"
            />
            <button
              type="button"
              onClick={() =>
                set(
                  "images",
                  f.images.filter((_, x) => x !== i),
                )
              }
              className="p-2 text-white/50"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => set("images", [...f.images, ""])}
          className="sa-btn sa-btn-outline text-xs"
        >
          <Plus size={14} /> Add Image
        </button>
      </div>

      <textarea
        rows={4}
        placeholder={"Features (one per line)"}
        className="sa-input"
        value={f.features_text}
        onChange={(e) => set("features_text", e.target.value)}
      ></textarea>
      <textarea
        rows={4}
        placeholder={"Specifications (Key: Value per line)"}
        className="sa-input"
        value={f.specs_text}
        onChange={(e) => set("specs_text", e.target.value)}
      ></textarea>
      <textarea
        rows={3}
        placeholder={"What's Included (one per line)"}
        className="sa-input"
        value={f.included_text}
        onChange={(e) => set("included_text", e.target.value)}
      ></textarea>
      <textarea
        rows={2}
        placeholder="Important Info"
        className="sa-input"
        value={f.important_info}
        onChange={(e) => set("important_info", e.target.value)}
      ></textarea>

      <div>
        <div className="overline text-white/50 mb-2">Tags</div>
        <div className="flex flex-wrap gap-2">
          {["best-seller", "new", "deal", "trending", "premium"].map((t) => (
            <label
              key={t}
              className={`px-3 py-1 border cursor-pointer text-xs ${f.tags.includes(t) ? "border-[#E63946] text-[#E63946]" : "border-white/15 text-white/70"}`}
            >
              <input
                type="checkbox"
                className="hidden"
                checked={f.tags.includes(t)}
                onChange={(e) =>
                  set(
                    "tags",
                    e.target.checked
                      ? [...f.tags, t]
                      : f.tags.filter((x) => x !== t),
                  )
                }
              />
              {t}
            </label>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={f.is_active}
          onChange={(e) => set("is_active", e.target.checked)}
        />{" "}
        Active
      </label>

      <div className="flex gap-3">
        <button className="sa-btn sa-btn-primary" data-testid="product-save">
          {initial ? "SAVE" : "CREATE"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="sa-btn sa-btn-outline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const PRODUCT_FILTERS = ["best-seller", "new", "deal", "trending", "premium"];

function ProductRow({
  product,
  categories,
  saving,
  onChange,
  onSave,
  onEditDetails,
  onDelete,
}) {
  const categoryOptions = categories.some((category) => category.name === product.category)
    ? categories
    : [{ name: product.category || "Interior Accessories", subcategories: [] }, ...categories];
  const activeCategory = categoryOptions.find((category) => category.name === product.category);
  const subcategories = activeCategory?.subcategories || [];
  const filterOptions = [...new Set([...PRODUCT_FILTERS, ...(product.tags || [])])];

  const update = (field, value) => onChange(product.id, { [field]: value });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave(product);
      }}
      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 p-4 border-b border-white/5 last:border-b-0"
      data-testid={`product-row-${product.id}`}
    >
      <div className="sm:col-span-2 xl:col-span-4 min-w-0">
        <div className="text-xs text-white/50 mb-2">Product images</div>
        {product.images?.length ? (
          <div className="flex flex-wrap gap-2">
            {product.images.map((image, index) => (
              <img
                key={`${image}-${index}`}
                src={image}
                alt={`${product.name || "Product"} image ${index + 1}`}
                loading="lazy"
                className="w-16 h-16 object-cover bg-black border border-white/10"
              />
            ))}
          </div>
        ) : (
          <div className="text-xs text-white/40">No images</div>
        )}
      </div>
      <label className="xl:col-span-2 text-xs text-white/50">
        Product name
        <input
          required
          value={product.name}
          onChange={(event) => update("name", event.target.value)}
          className="sa-input mt-1"
          aria-label="Product name"
        />
      </label>
      <label className="text-xs text-white/50">
        Price (₹)
        <input
          required
          min="0"
          step="0.01"
          type="number"
          value={product.price ?? ""}
          onChange={(event) => update("price", event.target.value)}
          className="sa-input mt-1"
          aria-label="Product price"
        />
      </label>
      <label className="text-xs text-white/50">
        MRP (₹)
        <input
          required
          min="0"
          step="0.01"
          type="number"
          value={product.mrp ?? ""}
          onChange={(event) => update("mrp", event.target.value)}
          className="sa-input mt-1"
          aria-label="Product MRP"
        />
      </label>
      <label className="text-xs text-white/50">
        Product type
        <select
          value={product.category}
          onChange={(event) => onChange(product.id, { category: event.target.value, subcategory: "" })}
          className="sa-input mt-1"
          aria-label="Product type"
        >
          {categoryOptions.map((category) => (
            <option key={category.name} value={category.name}>{category.name}</option>
          ))}
        </select>
      </label>
      <label className="text-xs text-white/50">
        Subcategory
        <select
          value={product.subcategory || ""}
          onChange={(event) => update("subcategory", event.target.value)}
          className="sa-input mt-1"
          aria-label="Product subcategory"
        >
          <option value="">None</option>
          {product.subcategory && !subcategories.includes(product.subcategory) && (
            <option value={product.subcategory}>{product.subcategory}</option>
          )}
          {subcategories.map((subcategory) => (
            <option key={subcategory} value={subcategory}>{subcategory}</option>
          ))}
        </select>
      </label>
      <fieldset className="xl:col-span-2 min-w-0">
        <legend className="text-xs text-white/50 mb-1">Store filters</legend>
        <div className="flex flex-wrap gap-x-3 gap-y-2">
          {filterOptions.map((filter) => (
            <label key={filter} className="flex items-center gap-1.5 text-xs text-white/70">
              <input
                type="checkbox"
                checked={(product.tags || []).includes(filter)}
                onChange={(event) => update(
                  "tags",
                  event.target.checked
                    ? [...(product.tags || []), filter]
                    : (product.tags || []).filter((tag) => tag !== filter),
                )}
                className="accent-[#E63946]"
              />
              {filter}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex items-end justify-between gap-3 xl:col-span-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSave({ ...product, is_active: product.is_active === false })}
            disabled={saving}
            className={`sa-btn sa-btn-outline px-3 py-2 text-xs ${product.is_active === false ? "text-emerald-400" : "text-white/70"}`}
            aria-label={`${product.is_active === false ? "Activate" : "Deactivate"} ${product.name || "product"}`}
            title={product.is_active === false ? "Activate product" : "Deactivate product"}
          >
            <Power size={15} />
            {product.is_active === false ? "ACTIVATE" : "DEACTIVATE"}
          </button>
          <button type="submit" disabled={saving} className="sa-btn sa-btn-primary px-3 py-2" aria-label="Save product" title="Save product">
            <Save size={15} />
            <span>{saving ? "SAVING" : "SAVE"}</span>
          </button>
          {!product._isDraft && (
            <button type="button" onClick={() => onEditDetails(product)} className="p-2 text-white/70 hover:text-white" aria-label="Edit product details" title="Edit images and details">
              <Edit size={16} />
            </button>
          )}
          <button type="button" onClick={() => onDelete(product)} className="p-2 text-white/70 hover:text-[#E63946]" aria-label="Delete product" title="Delete product">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </form>
  );
}

function ProductsAdmin() {
  const { categories } = useApp();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = () =>
    api.get("/admin/products").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);

  const save = async (payload) => {
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateProduct = (id, changes) => {
    setItems((current) => current.map((product) =>
      product.id === id ? { ...product, ...changes } : product,
    ));
  };

  const addProduct = () => {
    const id = `draft-${Date.now()}`;
    setItems((current) => [{
      id,
      _isDraft: true,
      name: "",
      price: "",
      mrp: "",
      category: categories[0]?.name || "Interior Accessories",
      subcategory: "",
      tags: [],
      stock: 0,
      images: [],
      features: [],
      specs: {},
      whats_included: [],
      is_active: true,
    }, ...current]);
    setShowForm(false);
    setEditing(null);
  };

  const saveInline = async (product) => {
    setSavingId(product.id);
    const payload = {
      name: product.name.trim(),
      price: Number(product.price),
      mrp: Number(product.mrp),
      category: product.category,
      subcategory: product.subcategory || "",
      tags: product.tags || [],
      description: product.description || "",
      features: product.features || [],
      specs: product.specs || {},
      whats_included: product.whats_included || [],
      important_info: product.important_info || "",
      brand: product.brand || "Street Auto",
      images: product.images || [],
      stock: Number(product.stock || 0),
      color: product.color || null,
      is_active: product.is_active !== false,
    };
    try {
      if (product._isDraft) {
        await api.post("/admin/products", payload);
      } else {
        await api.put(`/admin/products/${product.id}`, payload);
      }
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const deleteProduct = async (product) => {
    if (product._isDraft) {
      setItems((current) => current.filter((item) => item.id !== product.id));
      return;
    }
    await del(product.id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">
          Products{" "}
          <span className="text-white/40 text-lg">({items.length})</span>
        </h2>
        <button
          onClick={addProduct}
          className="sa-btn sa-btn-primary"
          data-testid="add-product-btn"
        >
          <Plus size={16} /> ADD PRODUCT
        </button>
      </div>

      {showForm && (
        <div className="mb-6">
          <ProductForm
            initial={editing}
            onSave={save}
            onCancel={() => {
              setShowForm(false);
              setEditing(null);
            }}
          />
        </div>
      )}

      <div className="bg-[#111] border border-white/10 divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-6 text-white/50">
            No products yet. Add a product to start building your catalog.
          </div>
        )}
        {items.map((p) => (
          <ProductRow
            key={p.id}
            product={p}
            categories={categories}
            saving={savingId === p.id}
            onChange={updateProduct}
            onSave={saveInline}
            onEditDetails={(product) => {
              setEditing(product);
              setShowForm(true);
            }}
            onDelete={deleteProduct}
          />
        ))}
      </div>
    </div>
  );
}

function OrdersAdmin() {
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/admin/orders").then((r) => setItems(r.data.items || []));
  }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}`, { order_status: status });
    setItems((prev) =>
      prev.map((o) => (o.id === id ? { ...o, order_status: status } : o)),
    );
    toast.success("Updated");
  };
  return (
    <div>
      <h2 className="font-display text-3xl mb-6">
        Orders <span className="text-white/40 text-lg">({items.length})</span>
      </h2>
      <div className="bg-[#111] border border-white/10 divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-6 text-white/50">No orders yet.</div>
        )}
        {items.map((o) => (
          <article key={o.id} className="p-4">
            <div className="grid md:grid-cols-6 gap-3 items-center">
              <div className="md:col-span-2">
                <div className="font-display">{o.order_number}</div>
                <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="text-sm">
                {o.shipping?.full_name}
                <div className="text-white/60 break-all">{o.shipping?.email}</div>
                <div className="text-white/50">{o.shipping?.phone} · {o.shipping?.city}, {o.shipping?.state}</div>
              </div>
              <div className="text-sm">{formatINR(o.total)}</div>
              <div className="text-sm">
                <span className={`px-2 py-0.5 border ${o.payment_status === "paid" ? "border-emerald-400/40 text-emerald-400" : "border-white/20"}`}>
                  {o.payment_status}
                </span>
              </div>
              <select
                value={o.order_status}
                onChange={(e) => setStatus(o.id, e.target.value)}
                className="sa-input py-2 text-sm"
                aria-label={`Update ${o.order_number} status`}
              >
                {["placed", "processing", "shipped", "delivered", "cancelled"].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <details className="mt-4 border-t border-white/10 pt-3">
              <summary className="cursor-pointer text-xs text-white/60 hover:text-white">
                Full customer and product details · {o.items?.length || 0} items
              </summary>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <section>
                  <h3 className="overline text-white/50 mb-2">Customer and delivery</h3>
                  <div className="text-sm">{o.shipping?.full_name}</div>
                  <div className="text-sm text-white/70">{o.shipping?.email}</div>
                  <div className="text-sm text-white/70">{o.shipping?.phone}</div>
                  <address className="text-sm text-white/60 not-italic mt-2">
                    {o.shipping?.address_line1}{o.shipping?.address_line2 ? `, ${o.shipping.address_line2}` : ""}<br />
                    {o.shipping?.city}, {o.shipping?.state} {o.shipping?.pincode}<br />
                    {o.shipping?.country}
                  </address>
                  {o.customer_id && <div className="text-xs text-white/40 mt-2">Account customer: {o.customer_id}</div>}
                </section>
                <section>
                  <h3 className="overline text-white/50 mb-2">Order totals</h3>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Subtotal</span><span>{formatINR(o.subtotal)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Shipping</span><span>{formatINR(o.shipping_cost)}</span></div>
                  <div className="flex justify-between text-sm mt-2 font-medium"><span>Total</span><span>{formatINR(o.total)}</span></div>
                  <div className="text-xs text-white/50 mt-2">Payment: {o.payment_status}</div>
                </section>
              </div>
              <section className="mt-5">
                <h3 className="overline text-white/50 mb-2">Products</h3>
                <ul className="divide-y divide-white/10">
                  {(o.items || []).map((item, index) => {
                    const images = item.images?.length ? item.images : item.image ? [item.image] : [];
                    return (
                      <li key={`${item.product_id}-${index}`} className="py-4 first:pt-2 flex gap-3">
                        {images.length > 0 && <div className="flex flex-wrap content-start gap-1 w-[68px] shrink-0">{images.map((image, imageIndex) => <button key={`${image}-${imageIndex}`} type="button" title={`Open ${item.name} image ${imageIndex + 1}`} onClick={() => setPreview({ image, alt: `${item.name} image ${imageIndex + 1}` })} className="w-8 h-8"><img src={image} alt={`${item.name} image ${imageIndex + 1}`} loading="lazy" className="w-full h-full object-cover bg-black border border-white/10" /></button>)}</div>}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-sm">{formatINR(item.price * item.quantity)}</div>
                          </div>
                          <div className="text-xs text-white/50 mt-1">Qty {item.quantity} · {formatINR(item.price)} each{item.brand ? ` · ${item.brand}` : ""}{item.category ? ` · ${item.category}` : ""}</div>
                          {item.description && <p className="text-xs text-white/60 mt-2 whitespace-pre-line">{item.description}</p>}
                          {item.features?.length > 0 && <ul className="list-disc pl-4 mt-2 text-xs text-white/60">{item.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>}
                          {Object.entries(item.specs || {}).map(([key, value]) => <div key={key} className="text-xs text-white/60 mt-1">{key}: {value}</div>)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </details>
          </article>
        ))}
      </div>
      <ImagePreview
        image={preview?.image}
        alt={preview?.alt || "Order product image"}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}

function QuestionsAdmin() {
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () =>
    api
      .get("/admin/expert-questions")
      .then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);
  const saveQuestion = async (question) => {
    if (question.show_in_faq && !question.answer?.trim()) {
      toast.error("Add an answer before publishing this FAQ");
      return;
    }
    setSavingId(question.id);
    try {
      const response = await api.put(
        `/admin/expert-questions/${question.id}`,
        {
          answer: question.answer || "",
          show_in_faq: Boolean(question.show_in_faq),
        },
      );
      setItems((current) =>
        current.map((item) =>
          item.id === question.id ? response.data : item,
        ),
      );
      toast.success("Response saved");
    } catch {
      toast.error("Could not save response");
    } finally {
      setSavingId(null);
    }
  };
  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/expert-questions/${questionId}`);
      setItems((current) => current.filter((item) => item.id !== questionId));
      toast.success("Question deleted");
    } catch {
      toast.error("Could not delete question");
    }
  };
  return (
    <div>
      <h2 className="font-display text-3xl mb-6">
        Expert Questions{" "}
        <span className="text-white/40 text-lg">({items.length})</span>
      </h2>
      <div className="space-y-3">
        {items.length === 0 && (
          <div className="bg-[#111] border border-white/10 p-6 text-white/50">
            No questions yet.
          </div>
        )}
        {items.map((q) => (
          <div key={q.id} className="bg-[#111] border border-white/10 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-display">{q.name}</div>
              <div className="text-xs text-white/50">
                {new Date(q.created_at).toLocaleString()}
              </div>
            </div>
            <div className="text-xs text-white/60 mt-1">
              {q.email} · {q.phone}
            </div>
            {(q.car_brand || q.car_model) && (
              <div className="text-xs text-white/60 mt-1">
                Car: {q.car_brand} {q.car_model} {q.car_year}
              </div>
            )}
            <div className="mt-3 text-sm">{q.question}</div>
            {(q.car_photo || q.product_photo) && (
              <div className="flex flex-wrap gap-3">
                {[
                  [q.car_photo, "Car photo"],
                  [q.product_photo, "Product/reference photo"],
                ]
                  .filter(([image]) => image)
                  .map(([image, label]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setPreview({ image, alt: label })}
                      className="text-left text-xs text-white/60"
                    >
                      <img
                        src={image}
                        alt={label}
                        className="mb-1 h-20 w-24 border border-white/10 object-cover"
                      />
                      {label}
                    </button>
                  ))}
              </div>
            )}
            {q.modification_type && (
              <div className="mt-2 overline text-[#E63946]">
                Looking to modify: {q.modification_type}
              </div>
            )}
            <textarea
              className="sa-input w-full"
              rows="3"
              placeholder="Write your response"
              value={q.answer || ""}
              onChange={(event) =>
                setItems((current) =>
                  current.map((item) =>
                    item.id === q.id
                      ? { ...item, answer: event.target.value }
                      : item,
                  ),
                )
              }
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={Boolean(q.show_in_faq)}
                  onChange={(event) =>
                    setItems((current) =>
                      current.map((item) =>
                        item.id === q.id
                          ? { ...item, show_in_faq: event.target.checked }
                          : item,
                      ),
                    )
                  }
                />
                Show on homepage FAQ
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="sa-btn sa-btn-primary"
                  disabled={savingId === q.id}
                  onClick={() => saveQuestion(q)}
                >
                  <Save size={14} /> {savingId === q.id ? "SAVING..." : "SAVE RESPONSE"}
                </button>
                <a
                  className={`sa-btn sa-btn-outline ${q.answer?.trim() ? "" : "pointer-events-none opacity-40"}`}
                  href={q.answer?.trim()
                    ? `mailto:${q.email}?subject=${encodeURIComponent(`Re: ${q.question}`)}&body=${encodeURIComponent(q.answer)}`
                    : undefined}
                >
                  <Mail size={14} /> EMAIL REPLY
                </a>
                <button
                  type="button"
                  className="sa-btn sa-btn-outline"
                  onClick={() => deleteQuestion(q.id)}
                  title="Delete question"
                >
                  <Trash2 size={14} /> DELETE
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ImagePreview
        image={preview?.image}
        alt={preview?.alt || "Question attachment"}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}

function SettingsAdmin() {
  const [s, setS] = useState(null);
  const { refreshSettings } = useApp();
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <div>Loading…</div>;
  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));
  const save = async () => {
    await api.put("/admin/settings", s);
    toast.success("Settings saved");
    refreshSettings();
  };
  const field = (k, label, type = "text") => (
    <label className="block">
      <span className="overline text-white/50">{label}</span>
      {type === "textarea" ? (
        <textarea
          rows={3}
          value={s[k] || ""}
          onChange={(e) => set(k, e.target.value)}
          className="sa-input mt-2"
        />
      ) : (
        <input
          type={type}
          value={s[k] ?? ""}
          onChange={(e) =>
            set(k, type === "number" ? Number(e.target.value) : e.target.value)
          }
          className="sa-input mt-2"
        />
      )}
    </label>
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">Site Settings</h2>
        <button
          onClick={save}
          className="sa-btn sa-btn-primary"
          data-testid="settings-save"
        >
          SAVE
        </button>
      </div>
      <div className="space-y-8">
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Announcement Bar</div>
          {field("announcement_bar", "Announcement Text")}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!s.announcement_enabled}
              onChange={(e) => set("announcement_enabled", e.target.checked)}
            />{" "}
            Show announcement bar
          </label>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Brand & Logo</div>
          {field("logo_url", "Logo URL")}
          {field("brand_name", "Brand Name")}
          {field("brand_suffix", "Brand Suffix")}
          {field("tagline", "Tagline")}
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Hero Section</div>
          {field("hero_image", "Hero Image URL")}
          <div className="grid grid-cols-3 gap-3">
            {field("hero_heading_1", "Heading Line 1")}
            {field("hero_heading_2", "Heading Line 2")}
            {field("hero_heading_3", "Heading Line 3")}
          </div>
          {field("hero_subtext", "Subtext", "textarea")}
          <div className="grid sm:grid-cols-2 gap-3">
            {field("hero_cta_primary", "Primary CTA Label")}
            {field("hero_cta_primary_url", "Primary CTA URL")}
            {field("hero_cta_secondary", "Secondary CTA Label")}
            {field("hero_cta_secondary_url", "Secondary CTA URL")}
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Promotional Banner</div>
          {field("promo_image", "Promo Image URL")}
          {field("promo_heading", "Promo Heading")}
          {field("promo_subtext", "Promo Subtext")}
          <div className="grid sm:grid-cols-2 gap-3">
            {field("promo_cta", "CTA Label")}
            {field("promo_cta_url", "CTA URL")}
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Contact</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {field("phone", "Phone")}
            {field("whatsapp", "WhatsApp")}
            {field("email", "Email")}
            {field("instagram", "Instagram")}
            {field("address", "Address")}
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">WhatsApp Notifications</div>
          <p className="text-xs text-white/50">
            Configure Meta WhatsApp Cloud API and approved templates. Both templates need exactly three body placeholders: order number/customer/total, and customer/phone/question.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {field("whatsapp_notification_recipient", "Notification recipient (include country code)", "tel")}
            {field("whatsapp_phone_number_id", "Cloud API phone number ID")}
            {field("whatsapp_access_token", "Cloud API access token", "password")}
            {field("whatsapp_api_version", "Graph API version")}
            {field("whatsapp_order_template", "Approved order template name")}
            {field("whatsapp_question_template", "Approved question template name")}
            {field("whatsapp_template_language", "Template language code")}
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Payments (Razorpay)</div>
          <p className="text-xs text-white/50">
            Leave empty for demo/mock checkout. Add real keys to enable live
            Razorpay payments.
          </p>
          {field("razorpay_key_id", "Razorpay Key ID")}
          {field("razorpay_key_secret", "Razorpay Key Secret")}
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Shipping</div>
          <div className="grid sm:grid-cols-3 gap-3">
            {field("shipping_flat_rate", "Flat Rate (₹)", "number")}
            {field("shipping_free_above", "Free Above (₹)", "number")}
            {field("shipping_estimate_days", "Estimate")}
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 p-6 space-y-3">
          <div className="overline text-[#E63946]">Policies</div>
          {field("shipping_policy", "Shipping Policy", "textarea")}
          {field("returns_policy", "Order Issues / Returns", "textarea")}
          {field("privacy_policy", "Privacy Policy", "textarea")}
          {field("terms_policy", "Terms & Conditions", "textarea")}
        </div>
        <button onClick={save} className="sa-btn sa-btn-primary">
          SAVE ALL
        </button>
      </div>
    </div>
  );
}

function NotifyMeAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/notify-me").then((r) => setItems(r.data.items || []));
  }, []);
  return (
    <div>
      <h2 className="font-display text-3xl mb-6">
        Notify-Me Requests ({items.length})
      </h2>
      <div className="bg-[#111] border border-white/10 divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-6 text-white/50">No requests yet.</div>
        )}
        {items.map((n) => (
          <div key={n.id} className="p-4 flex justify-between text-sm">
            <div>{n.email}</div>
            <div className="text-white/50">Product: {n.product_id}</div>
            <div className="text-white/50">
              {new Date(n.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsletterAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setItems(r.data.items || []));
  }, []);
  return (
    <div>
      <h2 className="font-display text-3xl mb-6">
        Newsletter Subscribers ({items.length})
      </h2>
      <div className="bg-[#111] border border-white/10 divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-6 text-white/50">No subscribers yet.</div>
        )}
        {items.map((n) => (
          <div key={n.id} className="p-4 flex justify-between text-sm">
            <div>{n.email}</div>
            <div className="text-white/50">
              {new Date(n.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportAdmin() {
  const [form, setForm] = useState({
    source_url: "https://b2b.autotrends.in/collections/new-products",
    max_pages: 1,
    activate: false,
    markup_pct: 40,
    price_markup_pct: 25,
    default_stock: 10,
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const doPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/admin/import/shopify/preview", form);
      setPreview(r.data);
      toast.success(
        `Fetched ${r.data.total_fetched} products (showing first 20)`,
      );
    } catch (e) {
      toast.error("Preview failed. Check the URL.");
    }
    setLoading(false);
  };

  const doImport = async () => {
    if (
      !window.confirm(
        `Import products from ${form.source_url}? They will land as drafts (inactive) unless "Activate" is checked.`,
      )
    )
      return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/admin/import/shopify", form);
      setResult(r.data);
      toast.success(
        `Imported ${r.data.imported} new products (${r.data.skipped_duplicates} duplicates skipped)`,
      );
    } catch (e) {
      toast.error("Import failed.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="import-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">Import Products</h2>
      </div>

      <div className="bg-[#111] border border-white/10 p-6 space-y-4">
        <div className="overline text-[#E63946]">
          Shopify Source (from a public /products.json store)
        </div>
        <p className="text-xs text-white/60">
          Paste a Shopify store URL or a specific collection URL. Products will
          be mapped to Street Auto categories, priced with your markup, and
          imported as drafts by default.
          <br />
          Examples:{" "}
          <code className="text-white/80">
            https://b2b.autotrends.in
          </code> or{" "}
          <code className="text-white/80">
            https://b2b.autotrends.in/collections/new-products
          </code>
        </p>

        <input
          className="sa-input"
          value={form.source_url}
          onChange={(e) => set("source_url", e.target.value)}
          data-testid="import-url"
        />

        <div className="grid sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="overline text-white/50">
              Pages to fetch (×250)
            </span>
            <input
              type="number"
              min={1}
              max={20}
              className="sa-input mt-2"
              value={form.max_pages}
              onChange={(e) => set("max_pages", Number(e.target.value))}
              data-testid="import-pages"
            />
          </label>
          <label className="block">
            <span className="overline text-white/50">Selling markup %</span>
            <input
              type="number"
              className="sa-input mt-2"
              value={form.price_markup_pct}
              onChange={(e) => set("price_markup_pct", Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="overline text-white/50">MRP markup %</span>
            <input
              type="number"
              className="sa-input mt-2"
              value={form.markup_pct}
              onChange={(e) => set("markup_pct", Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="overline text-white/50">Default stock</span>
            <input
              type="number"
              className="sa-input mt-2"
              value={form.default_stock}
              onChange={(e) => set("default_stock", Number(e.target.value))}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.activate}
            onChange={(e) => set("activate", e.target.checked)}
            className="accent-[#E63946]"
          />
          Publish immediately (skip draft state and make live)
        </label>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={doPreview}
            disabled={loading}
            className="sa-btn sa-btn-outline"
            data-testid="import-preview-btn"
          >
            {loading ? "LOADING..." : "PREVIEW"}
          </button>
          <button
            onClick={doImport}
            disabled={loading}
            className="sa-btn sa-btn-primary"
            data-testid="import-run-btn"
          >
            <Download size={16} /> {loading ? "IMPORTING..." : "IMPORT NOW"}
          </button>
        </div>
      </div>

      {result && (
        <div
          className="mt-6 bg-emerald-900/10 border border-emerald-400/30 p-6"
          data-testid="import-result"
        >
          <div className="overline text-emerald-400 mb-2">Import Complete</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-white/50 text-xs">Imported</div>
              <div className="font-display text-2xl text-emerald-400">
                {result.imported}
              </div>
            </div>
            <div>
              <div className="text-white/50 text-xs">Skipped (duplicates)</div>
              <div className="font-display text-2xl">
                {result.skipped_duplicates}
              </div>
            </div>
            <div>
              <div className="text-white/50 text-xs">Errors</div>
              <div className="font-display text-2xl">{result.errors}</div>
            </div>
            <div>
              <div className="text-white/50 text-xs">Total fetched</div>
              <div className="font-display text-2xl">
                {result.total_fetched}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/70">
            Go to <b>Products</b> tab to review — products are{" "}
            {form.activate ? "LIVE now" : "drafts (inactive)"}. Rewrite
            titles/descriptions in your voice before publishing.
          </p>
        </div>
      )}

      {preview && (
        <div className="mt-6">
          <div className="overline text-white/50 mb-3">
            Preview ({preview.total_fetched} products found, showing{" "}
            {preview.preview.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {preview.preview.map((p, i) => (
              <div
                key={p.slug || i}
                className="bg-[#111] border border-white/10 p-3"
              >
                <div className="aspect-square bg-black mb-2 overflow-hidden">
                  {p.images?.[0] && (
                    <img
                      src={p.images[0]}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="text-xs line-clamp-2 min-h-[2rem]">
                  {p.name}
                </div>
                <div className="mt-1 text-[#E63946] font-bold text-sm">
                  ₹{p.price.toLocaleString("en-IN")}
                </div>
                <div className="text-[10px] text-white/40">{p.category}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const buildCategories = [
  "Exterior",
  "Interior",
  "Wheels & Tyres",
  "Lighting",
  "Wrap / Paint",
  "Suspension",
  "Exhaust",
  "Performance",
  "ECU Remap",
];

function BuildForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(() => ({
    make: "",
    model: "",
    year: "",
    title: "Complete Transformation",
    description: "",
    categories: [],
    cover_image: "",
    images: [],
    cosmetic_modifications: "",
    interior_modifications: "",
    performance_modifications: "",
    ecu_work: "",
    other_work: "",
    project_duration: "",
    location: "",
    is_published: false,
    is_featured: false,
    display_order: 0,
    ...(initial || {}),
  }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = (e) => {
    e.preventDefault();
    onSave({
      ...f,
      images: f.images.map((img, i) => ({ ...img, display_order: i })),
    });
  };
  const addImage = () =>
    set("images", [
      ...f.images,
      {
        url: "",
        caption: "",
        image_type: "general",
        display_order: f.images.length,
      },
    ]);
  const uploadImages = async (event) => {
    for (const file of Array.from(event.target.files || [])) {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post("/admin/build-images", body);
      setF((old) => ({
        ...old,
        images: [
          ...old.images,
          {
            id: response.data.id,
            url: response.data.url,
            caption: "",
            image_type: "general",
            display_order: old.images.length,
          },
        ],
      }));
    }
  };
  const removeImage = async (index) => {
    const image = f.images[index];
    set(
      "images",
      f.images.filter((_, i) => i !== index),
    );
    if (image?.url === f.cover_image) set("cover_image", f.images.find((_, i) => i !== index)?.url || "");
    if (image?.id)
      await api.delete(`/admin/build-images/${image.id}`).catch(() => {});
  };
  const replaceImage = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    const response = await api.post("/admin/build-images", body);
    const old = f.images[index];
    const next = [...f.images];
    next[index] = { ...old, id: response.data.id, url: response.data.url };
    set("images", next);
    if (old?.url === f.cover_image) set("cover_image", response.data.url);
    if (old?.id)
      await api.delete(`/admin/build-images/${old.id}`).catch(() => {});
  };
  const moveImage = (from, to) => {
    if (to < 0 || to >= f.images.length) return;
    const next = [...f.images];
    const [image] = next.splice(from, 1);
    next.splice(to, 0, image);
    set("images", next);
  };
  return (
    <form
      onSubmit={save}
      className="bg-[#111] border border-white/10 p-6 space-y-4"
    >
      <div className="flex justify-between">
        <h3 className="font-display text-2xl">
          {initial ? "EDIT BUILD" : "ADD NEW BUILD"}
        </h3>
        <button type="button" onClick={onCancel}>
          <X size={18} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          className="sa-input"
          placeholder="Car make"
          required
          value={f.make}
          onChange={(e) => set("make", e.target.value)}
        />
        <input
          className="sa-input"
          placeholder="Car model"
          required
          value={f.model}
          onChange={(e) => set("model", e.target.value)}
        />
        <input
          className="sa-input"
          placeholder="Year"
          value={f.year}
          onChange={(e) => set("year", e.target.value)}
        />
        <input
          className="sa-input"
          placeholder="Build title"
          value={f.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </div>
      <textarea
        className="sa-input"
        rows="3"
        placeholder="Overview / description"
        value={f.description}
        onChange={(e) => set("description", e.target.value)}
      />
      <div>
        <div className="overline text-white/50 mb-2">
          MODIFICATION CATEGORIES
        </div>
        <div className="flex flex-wrap gap-2">
          {buildCategories.map((c) => (
            <label
              key={c}
              className={`px-3 py-2 border text-xs cursor-pointer ${f.categories.includes(c) ? "border-[#E63946] text-[#E63946]" : "border-white/15"}`}
            >
              <input
                type="checkbox"
                className="hidden"
                checked={f.categories.includes(c)}
                onChange={(e) =>
                  set(
                    "categories",
                    e.target.checked
                      ? [...f.categories, c]
                      : f.categories.filter((x) => x !== c),
                  )
                }
              />
              {c}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <div>
            <div className="overline text-white/50">GALLERY IMAGES</div>
            <div className="text-xs text-white/40 mt-1">Drag photos to reorder. Select the star to set the cover.</div>
          </div>
          <label className="sa-btn sa-btn-primary text-xs cursor-pointer">
            <Upload size={14} /> UPLOAD PHOTOS
            <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} />
          </label>
        </div>
        {f.images.map((img, i) => (
          <div
            key={i}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => moveImage(Number(e.dataTransfer.getData("text/plain")), i)}
            className="bg-[#0A0A0A] border border-white/10 p-3 mb-3"
          >
            <div className="flex gap-3 items-start">
              <GripVertical size={18} className="text-white/30 mt-2 shrink-0" />
              <img src={mediaUrl(img.url)} alt={img.caption || "Build upload"} className="w-24 h-20 object-cover bg-black shrink-0" />
              <div className="flex-1 grid sm:grid-cols-[1fr_150px_auto_auto] gap-2 items-center">
                <input className="sa-input" placeholder="Caption" value={img.caption || ""} onChange={(e) => { const n = [...f.images]; n[i] = { ...n[i], caption: e.target.value }; set("images", n); }} />
                <select className="sa-input" value={img.image_type || "general"} onChange={(e) => { const n = [...f.images]; n[i] = { ...n[i], image_type: e.target.value }; set("images", n); }}>
                  {["general", "before", "during", "after"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <button type="button" title="Set as cover" onClick={() => set("cover_image", img.url)} className={`p-2 ${f.cover_image === img.url ? "text-[#E63946]" : "text-white/40"}`}><Star size={18} fill={f.cover_image === img.url ? "currentColor" : "none"} /></button>
                <button type="button" title="Delete image" onClick={() => removeImage(i)} className="p-2 text-white/60 hover:text-[#E63946]"><Trash2 size={16} /></button>
                <label className="sa-btn sa-btn-outline text-xs cursor-pointer sm:col-span-4"><Upload size={14} /> REPLACE IMAGE<input type="file" accept="image/*" className="hidden" onChange={(e) => replaceImage(i, e)} /></label>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          ["cosmetic_modifications", "Cosmetic modifications"],
          ["interior_modifications", "Interior modifications"],
          ["performance_modifications", "Performance modifications"],
          ["ecu_work", "ECU / remap work"],
          ["other_work", "Other work"],
          ["project_duration", "Project duration"],
          ["location", "Location"],
        ].map(([k, p]) => (
          <textarea
            key={k}
            className="sa-input"
            rows="2"
            placeholder={p}
            value={f[k] || ""}
            onChange={(e) => set(k, e.target.value)}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.is_published}
            onChange={(e) => set("is_published", e.target.checked)}
          />{" "}
          Published
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.is_featured}
            onChange={(e) => set("is_featured", e.target.checked)}
          />{" "}
          Feature on homepage
        </label>
        <input
          className="sa-input max-w-[140px]"
          type="number"
          placeholder="Order"
          value={f.display_order}
          onChange={(e) => set("display_order", Number(e.target.value))}
        />
      </div>
      <div className="flex gap-3">
        <button className="sa-btn sa-btn-primary">SAVE BUILD</button>
        <button
          type="button"
          onClick={onCancel}
          className="sa-btn sa-btn-outline"
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}

function BuildsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const load = () =>
    api.get("/admin/builds").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);
  const save = async (payload) => {
    try {
      if (editing) await api.put(`/admin/builds/${editing.id}`, payload);
      else await api.post("/admin/builds", payload);
      toast.success("Build saved");
      setShow(false);
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save build");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this build and all its gallery metadata?"))
      return;
    await api.delete(`/admin/builds/${id}`);
    load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">
          Our Builds{" "}
          <span className="text-white/40 text-lg">({items.length})</span>
        </h2>
        <button
          className="sa-btn sa-btn-primary"
          onClick={() => {
            setEditing(null);
            setShow(true);
          }}
        >
          <Plus size={16} /> ADD NEW BUILD
        </button>
      </div>
      {show && (
        <div className="mb-6">
          <BuildForm
            initial={editing}
            onSave={save}
            onCancel={() => {
              setShow(false);
              setEditing(null);
            }}
          />
        </div>
      )}
      <div className="bg-[#111] border border-white/10 divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-6 text-white/50">
            No builds yet. Add your first transformation.
          </div>
        )}
        {items.map((b) => (
          <div key={b.id} className="p-4 flex items-center gap-4">
            <div className="w-20 h-14 bg-black">
              {(b.cover_image || b.images?.[0]?.url) && (
                <img
                  src={mediaUrl(b.cover_image || b.images[0].url)}
                  className="w-full h-full object-cover"
                  alt=""
                />
              )}
            </div>
            <div className="flex-1">
              <div className="font-display">
                {b.make} {b.model}
              </div>
              <div className="text-xs text-white/50">
                {b.title} · {b.images?.length || 0} photos ·{" "}
                <span
                  className={
                    b.is_published ? "text-emerald-400" : "text-white/40"
                  }
                >
                  {b.is_published ? "Published" : "Draft"}
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                setEditing(b);
                setShow(true);
              }}
              className="p-2"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => remove(b.id)}
              className="p-2 text-white/60 hover:text-[#E63946]"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BuildEnquiriesAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/build-enquiries").then((r) => setItems(r.data.items || []));
  }, []);
  const statuses = [
    "new",
    "contacted",
    "consultation",
    "quotation sent",
    "approved",
    "completed",
    "closed",
  ];
  const update = async (id, status) => {
    await api.put(`/admin/build-enquiries/${id}`, { status });
    setItems(items.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  return (
    <div>
      <h2 className="font-display text-3xl mb-6">
        Build Enquiries{" "}
        <span className="text-white/40 text-lg">({items.length})</span>
      </h2>
      <div className="space-y-3">
        {items.length === 0 && (
          <div className="bg-[#111] border border-white/10 p-6 text-white/50">
            No build enquiries yet.
          </div>
        )}
        {items.map((q) => (
          <div key={q.id} className="bg-[#111] border border-white/10 p-5">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <div className="font-display text-xl">{q.name}</div>
                <div className="text-xs text-white/50">
                  {q.email} · {q.phone} · {q.city}
                </div>
              </div>
              <select
                className="sa-input py-2 w-auto"
                value={q.status}
                onChange={(e) => update(q.id, e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="text-sm mt-4 text-white/75">
              {q.car_brand} {q.car_model} {q.model_year} · {q.budget}
            </div>
            <div className="text-xs text-[#E63946] mt-2">
              {(q.services || []).join(" · ")}
            </div>
            <p className="text-sm text-white/60 mt-3 whitespace-pre-line">
              {q.vision}
            </p>
            <div className="text-xs text-white/40 mt-3">
              {new Date(q.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => { setCats(r.data.categories || []); setOverrides(r.data.overrides || {}); });
  useEffect(() => { load(); }, []);

  const update = async (slug, field, value) => {
    setBusy(slug);
    try {
      await api.put("/admin/categories", { slug, [field]: value });
      toast.success("Category updated");
      load();
    } catch { toast.error("Save failed"); }
    setBusy(null);
  };

  return (
    <div data-testid="categories-admin">
      <h2 className="font-display text-3xl mb-6">Categories</h2>
      <p className="text-sm text-white/60 mb-6">Click "Change Image" to swap any category tile on the homepage. Default images come from the backend; your overrides take precedence.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {cats.map((c) => (
          <div key={c.slug} className="bg-[#111] border border-white/10 p-4 flex gap-4" data-testid={`cat-${c.slug}`}>
            <div className="w-32 h-32 bg-black flex-shrink-0 overflow-hidden">
              {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl">{c.name}</div>
              <div className="text-xs text-white/60 mt-1 line-clamp-2">{c.description}</div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    const url = window.prompt("Paste the new image URL (full https://... link):", c.image || "");
                    if (url && url.trim()) update(c.slug, "image", url.trim());
                  }}
                  disabled={busy === c.slug}
                  className="sa-btn sa-btn-outline text-xs py-2 px-3"
                  data-testid={`cat-img-${c.slug}`}
                >
                  <ImageIcon size={14}/> Change Image
                </button>
                <button
                  onClick={() => {
                    const d = window.prompt("New description:", c.description || "");
                    if (d !== null) update(c.slug, "description", d);
                  }}
                  disabled={busy === c.slug}
                  className="sa-btn sa-btn-outline text-xs py-2 px-3"
                >
                  <Edit size={14}/> Edit Text
                </button>
              </div>
              {overrides[c.slug] && <div className="mt-2 overline text-emerald-400">OVERRIDDEN</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AIRewriteAdmin() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [limit, setLimit] = useState(10);
  const [model, setModel] = useState("gpt-5.2");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/products", { params: { limit: 500 } }).then((r) => setProducts((r.data.items || []).filter((p) => !(p.features && p.features.length))));
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const previewOne = async (p) => {
    setRunning(true);
    try {
      const r = await api.post(`/admin/products/${p.id}/rewrite`, null, { params: { apply: false, model } });
      setPreview({ before: p, after: r.data.preview });
    } catch { toast.error("Preview failed"); }
    setRunning(false);
  };

  const applyPreview = async () => {
    if (!preview) return;
    setRunning(true);
    try {
      await api.post(`/admin/products/${preview.before.id}/rewrite`, null, { params: { apply: true, model } });
      toast.success("Applied");
      setPreview(null);
      load();
    } catch { toast.error("Apply failed"); }
    setRunning(false);
  };

  const runBulk = async () => {
    const ids = Array.from(selected);
    const payload = { product_ids: ids.length ? ids : undefined, limit: ids.length || limit, model };
    if (!window.confirm(`Rewrite ${ids.length || limit} product${(ids.length || limit) === 1 ? "" : "s"} using ${model}? This updates them in place.`)) return;
    setRunning(true); setResult(null);
    try {
      const r = await api.post("/admin/rewrite/bulk", payload);
      setResult(r.data);
      toast.success(`Rewrote ${r.data.rewritten} products`);
      setSelected(new Set()); load();
    } catch { toast.error("Bulk rewrite failed"); }
    setRunning(false);
  };

  return (
    <div data-testid="ai-rewrite-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">AI Rewrite <span className="text-white/40 text-lg">({products.length} unrewritten)</span></h2>
      </div>
      <p className="text-sm text-white/60 mb-4">Clean up supplier-style titles and descriptions in Street Auto brand voice using the Emergent LLM key.</p>

      <div className="bg-[#111] border border-white/10 p-5 mb-6 flex flex-wrap gap-3 items-end">
        <label className="flex-1 min-w-[160px]">
          <span className="overline text-white/50">Model</span>
          <select value={model} onChange={(e) => setModel(e.target.value)} className="sa-input mt-2">
            <option value="gpt-5.2">gpt-5.2 (fast, cheap)</option>
            <option value="gpt-5.4-mini">gpt-5.4-mini</option>
            <option value="gpt-5.4">gpt-5.4 (quality)</option>
            <option value="claude-haiku-4-5-20251001">claude-haiku-4-5</option>
          </select>
        </label>
        <label className="w-32">
          <span className="overline text-white/50">Batch size</span>
          <input type="number" min={1} max={50} value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="sa-input mt-2" />
        </label>
        <button onClick={runBulk} disabled={running} className="sa-btn sa-btn-primary" data-testid="rewrite-run">
          <Sparkles size={16}/> {running ? "REWRITING..." : (selected.size ? `REWRITE ${selected.size} SELECTED` : `REWRITE OLDEST ${limit}`)}
        </button>
      </div>

      {result && (
        <div className="mb-6 bg-emerald-900/10 border border-emerald-400/30 p-5">
          <div className="overline text-emerald-400 mb-2">Done</div>
          Rewrote {result.rewritten} / scanned {result.scanned} (errors: {result.errors})
        </div>
      )}

      {preview && (
        <div className="mb-6 bg-[#111] border border-[#E63946]/40 p-5">
          <div className="overline text-[#E63946] mb-3">Preview — not yet applied</div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="overline text-white/50 mb-1">BEFORE</div>
              <div className="font-display">{preview.before.name}</div>
              <div className="text-xs text-white/60 mt-1 line-clamp-6">{preview.before.description}</div>
            </div>
            <div>
              <div className="overline text-emerald-400 mb-1">AFTER</div>
              <div className="font-display">{preview.after.name}</div>
              <div className="text-xs text-white/80 mt-1">{preview.after.description}</div>
              <ul className="mt-2 text-xs text-white/70 list-disc pl-4">{preview.after.features.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={applyPreview} disabled={running} className="sa-btn sa-btn-primary">APPLY</button>
            <button onClick={() => setPreview(null)} className="sa-btn sa-btn-outline">Discard</button>
          </div>
        </div>
      )}

      <div className="bg-[#111] border border-white/10 divide-y divide-white/5 max-h-[500px] overflow-y-auto">
        {products.length === 0 && <div className="p-6 text-white/50">No unrewritten products. Imported products with no features yet are shown here.</div>}
        {products.slice(0, 100).map((p) => (
          <div key={p.id} className="p-3 flex items-center gap-3">
            <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="accent-[#E63946]" />
            <div className="w-10 h-10 bg-black flex-shrink-0">{p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt=""/>}</div>
            <div className="flex-1 min-w-0 text-sm line-clamp-1">{p.name}</div>
            <button onClick={() => previewOne(p)} disabled={running} className="text-xs text-white/60 hover:text-[#E63946]">Preview</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null); // null = checking, true/false = decided
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/me")
      .then(() => {
        if (!cancelled) setAuthed(true);
      })
      .catch(() => {
        if (!cancelled) setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authed === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50">
        Checking session…
      </div>
    );
  if (!authed) return <AdminLogin onAuthed={() => setAuthed(true)} />;

  const logout = () => {
    api.post("/admin/logout").catch(() => {});
    setAuthed(false);
    navigate("/");
  };

  const nav = [
    { to: "/admin", label: "Products", icon: Package, end: true },
    { to: "/admin/rewrite", label: "AI Rewrite", icon: Sparkles },
    { to: "/admin/import", label: "Import", icon: Download },
    { to: "/admin/categories", label: "Categories", icon: ImageIcon },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
    { to: "/admin/questions", label: "Questions", icon: MessageSquare },
    { to: "/admin/notify", label: "Notify Me", icon: Bell },
    { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <div className="grid md:grid-cols-[240px_1fr]">
        <aside className="border-b border-white/10 md:border-b-0 md:border-r p-4 md:p-6 flex flex-col md:min-h-screen">
          <Link to="/" className="font-display text-xl mb-4 md:mb-8">
            STREET AUTO<span className="text-[#E63946]">.</span>
          </Link>
          <div className="overline text-white/40 mb-2 md:mb-4">Admin</div>
          <nav className="flex gap-2 overflow-x-auto md:block md:space-y-1 flex-1">
            {nav.map((n) => {
              const active = n.end
                ? loc.pathname === n.to
                : loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex shrink-0 items-center gap-3 whitespace-nowrap px-3 py-2 text-sm ${active ? "bg-white/5 text-white border-l-2 border-[#E63946]" : "text-white/70 hover:bg-white/5"}`}
                >
                  <n.icon size={16} /> {n.label}
                </Link>
              );
            })}
          </nav>
          <button
            onClick={logout}
            className="sa-btn sa-btn-outline text-xs mt-6"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> LOGOUT
          </button>
        </aside>
        <main className="p-4 md:p-10">
          <Routes>
            <Route index element={<ProductsAdmin />} />
            <Route path="rewrite" element={<AIRewriteAdmin />} />
            <Route path="import" element={<ImportAdmin />} />
            <Route path="categories" element={<CategoriesAdmin />} />
            <Route path="orders" element={<OrdersAdmin />} />
            <Route path="questions" element={<QuestionsAdmin />} />
            <Route path="notify" element={<NotifyMeAdmin />} />
            <Route path="newsletter" element={<NewsletterAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
